import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { OwnerMetricDashboard } from "@/components/owner-metrics/owner-metric-dashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Route as authenticatedRoute } from "../_authenticated";

interface MeOwner {
  id: string;
  name: string;
  email: string;
  hubspotOwnerId: string;
}

interface Pipeline {
  id: string;
  label: string;
  stages: { id: string; label: string }[];
}

export const Route = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/my-dashboard",
  component: MyDashboardPage,
});

function MyDashboardPage() {
  const [owner, setOwner] = useState<MeOwner | null | undefined>(undefined);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [ownerRes, pipelinesRes] = await Promise.all([
        fetch("/api/me/owner"),
        fetch("/api/hubspot/pipelines/deals"),
      ]);

      if (ownerRes.ok) {
        const data = await ownerRes.json();
        setOwner(data.owner);
      } else {
        setOwner(null);
      }

      if (pipelinesRes.ok) {
        const data = await pipelinesRes.json();
        setPipelines(data.results ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={`sk-${i}`} className="py-5">
              <CardContent className="flex flex-col gap-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!owner) {
    return (
      <Card className="py-12">
        <CardContent className="text-center text-muted-foreground">
          <p className="text-lg font-medium mb-2">No owner record linked to your account</p>
          <p className="text-sm">
            Contact an administrator to link your account to an owner record.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <OwnerMetricDashboard pipelines={pipelines} ownerId={owner.hubspotOwnerId} hideBackButton />
  );
}
